
import React, { useState, useEffect } from 'react';
import { Page } from '@/types/crawler';
import { usePageAnalysis } from '@/hooks/usePageAnalysis';
import { toast } from 'sonner';
import { logAnalysisResult } from '@/services/deepAnalysisService';

interface AnalysisEngineProps {
  selectedPage: string | null;
  pageObject: Page | null;
  children: (props: AnalysisEngineState) => React.ReactNode;
}

export interface AnalysisEngineState {
  isAnalyzing: boolean;
  analysisComplete: boolean;
  pageAnalysisResult: any;
  analysisError: any;
  hasError: boolean;
  errorMessage: string | null;
  handleAnalyze: (pageObj?: Page) => void;
  handleErrorRetry: () => void;
}

const AnalysisEngine: React.FC<AnalysisEngineProps> = ({
  selectedPage,
  pageObject,
  children
}) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const {
    isAnalyzing,
    analysisComplete,
    result: pageAnalysisResult,
    error: analysisError,
    analyzePage,
    getPageAnalysis
  } = usePageAnalysis();

  useEffect(() => {
    if (selectedPage && pageObject) {
      setHasError(false);
      setErrorMessage(null);
      
      try {
        // Log the page being analyzed for debugging
        console.log(`Auto-analyzing page: ${pageObject.url} (${pageObject.content?.length || 0} chars)`);
        getPageAnalysis(pageObject);
      } catch (error) {
        console.error("Error getting page analysis:", error);
        toast.info("Page may be inaccessible or have errors. Attempting analysis anyway.");
      }
    }
  }, [selectedPage, pageObject, getPageAnalysis]);

  // Log analysis results when they become available
  useEffect(() => {
    if (analysisComplete && pageAnalysisResult) {
      console.log('Analysis completed, logging results:');
      logAnalysisResult(pageAnalysisResult);
    }
  }, [analysisComplete, pageAnalysisResult]);

  // Handle analyze button click for a single page
  const handleAnalyze = (pageObj?: Page) => {
    if (!pageObj) {
      setHasError(true);
      setErrorMessage('Unable to analyze page: No page object provided');
      toast.error('Unable to analyze page');
      return;
    }
    
    setHasError(false);
    setErrorMessage(null);
    
    try {
      console.log('Starting manual analysis for:', pageObj.url);
      analyzePage(pageObj);
    } catch (error) {
      console.error("Error analyzing page:", error);
      setHasError(true);
      setErrorMessage(`Error analyzing page: ${(error as Error).message}`);
      toast.error(`Analysis error: ${(error as Error).message}`);
    }
  };

  // Handle errors in analysis result
  const handleErrorRetry = () => {
    if (pageObject) {
      setHasError(false);
      setErrorMessage(null);
      
      try {
        console.log('Retrying analysis for:', pageObject.url);
        analyzePage(pageObject);
        toast('Retrying analysis...', {
          description: 'Please wait while we analyze the page again.'
        });
      } catch (error) {
        console.error("Error during retry:", error);
        setHasError(true);
        setErrorMessage(`Retry failed: ${(error as Error).message}`);
        toast.error(`Retry failed: ${(error as Error).message}`);
      }
    }
  };

  return (
    <>
      {children({
        isAnalyzing,
        analysisComplete,
        pageAnalysisResult,
        analysisError,
        hasError,
        errorMessage,
        handleAnalyze,
        handleErrorRetry
      })}
    </>
  );
};

export default AnalysisEngine;
